import { isApiTrue } from "@/utils/apiBoolean";
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CButton,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CRow,
  CCol,
  CNav,
  CNavItem,
  CNavLink,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
} from "@coreui/react";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClose,
  faTrash,
  faUserSlash,
  faUserCheck,
  faBan,
  faUnlock,
} from "@fortawesome/free-solid-svg-icons";
import { Formik } from "formik";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import TNTable from "@/components/TNTable";
import CustomDropdown from "@/components/CustomDropdown";
import { reportReasonService } from "@/api";
import UserMasterService from "@/api/userMasterService";
import { showRestrictDialog } from "@/components/UserActionDialogs";
import { showConfirmDialog } from "@/components/contentModeration/moderationDialogs";
import { userRestriction } from "@/constants/translation/userRestriction.translation";
import { useTranslationContext } from "@/context/TranslationContext";
import reportedUsersTranslations from "@/constants/translation/reportedUsers.translation";
import userTranslations from "@/constants/translation/userMaster.translation";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import UserMasterValidationClass from "@/helpers/validations/userMaster";
import { dateTimeFormat } from "@/utils/dateFormate";
import constants from "@/libs/constant";
import { masters } from "@/libs/routes";

const reportedUsers = reportedUsersTranslations.list;
const user = userTranslations;
const { status, pagination: paginationConstants } = constants;

// The two sub-tabs of this screen. "user" is the original reported-users table; "feed" is the
// combined "Reported Post and Stories" tab - one list covering both content types, since
// comments are not reportable and there is no separate per-type tab.
const REPORT_TAB = {
  USERS: "user",
  FEED: "feed",
};

const filterInputStyle = {
  backgroundColor: "#212631",
  borderColor: "#212631",
  color: "white",
};

const getReportDescription = (row) => {
  if (row.reason_title === "Other") {
    return row.report_description || row.reportDescription || null;
  }
  return row.report_description || row.reason_description || null;
};

const TruncateCell = ({ text, className = "reported-users-cell-text" }) => (
  <span className={className} title={text}>
    {text}
  </span>
);

const MAX_DESC_LENGTH = 80;

const DescriptionCell = ({ text }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) {
    return <span style={{ wordBreak: "break-word", whiteSpace: "normal", lineHeight: "1.3" }}>{text}</span>;
  }

  const isLong = text.length > MAX_DESC_LENGTH;

  return (
    <span style={{ wordBreak: "break-word", whiteSpace: "normal", lineHeight: "1.3" }}>
      {expanded || !isLong ? text : `${text.slice(0, MAX_DESC_LENGTH)}...`}
      {isLong && (
        <>
          {" "}
          <button
            onClick={() => setExpanded((prev) => !prev)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "var(--cui-primary, #0d6efd)",
              fontSize: "inherit",
              fontWeight: "600",
              textDecoration: "underline",
            }}
          >
            {expanded ? "Read less" : "Read more"}
          </button>
        </>
      )}
    </span>
  );
};

// Reported content preview: a thumbnail (posts/stories with an image) or a type icon, plus the
// caption/text so a moderator can see what was reported without leaving the page. The thumbnail
// opens a modal with the full-size image; the text gets the same expand/collapse "Read more"
// treatment as the report description column, instead of the silent 2-line clamp that made this
// unreadable when the badge/thumbnail/text were all squeezed into one narrow cell.
const ReportedContentCell = ({ row, t }) => {
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const contentType = row.content_type;
  const isStory = contentType === "story";
  const typeLabel = isStory
    ? t(reportedUsers.content_type.story)
    : t(reportedUsers.content_type.post);
  const hasImage = Boolean(row.content_image_url);

  return (
    <>
      <div className="d-flex align-items-start gap-2">
        {hasImage ? (
          <button
            type="button"
            onClick={() => setImageModalOpen(true)}
            className="reported-content-thumb-btn"
            title={t(reportedUsers.view_image_tooltip)}
            style={{
              width: 40,
              height: 40,
              padding: 0,
              border: "none",
              borderRadius: 6,
              flexShrink: 0,
              cursor: "pointer",
              overflow: "hidden",
            }}
          >
            <img
              src={row.content_image_url}
              alt={typeLabel}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </button>
        ) : null /* No image at all (caption story or text-only post) - no placeholder icon
                     either, just the caption/text below. */}
        <div className="d-flex flex-column" style={{ minWidth: 0 }}>
          <span className="badge bg-info text-dark align-self-start mb-1">{typeLabel}</span>
          <DescriptionCell text={row.content_preview || t(reportedUsers.defaults.na)} />
        </div>
      </div>

      {hasImage && (
        <CModal
          visible={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          alignment="center"
        >
          <CModalHeader>
            <CModalTitle>{typeLabel}</CModalTitle>
          </CModalHeader>
          <CModalBody className="text-center">
            <img
              src={row.content_image_url}
              alt={typeLabel}
              style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 4 }}
            />
            {row.content_preview && (
              <p className="mt-3 text-start" style={{ wordBreak: "break-word" }}>
                {row.content_preview}
              </p>
            )}
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const formatReportDateTime = (row) => {
  const updatedAt =
    row?.updated_at ?? row?.updatedAt ?? row?.created_at ?? row?.createdAt ?? null;
  if (!updatedAt) {
    return null;
  }
  return dateTimeFormat(updatedAt);
};

const ReportedUsersList = () => {
  const t = useTranslationContext();

  // The `tab` query param is what App.jsx's ReportedUsersRoute reads to resolve the breadcrumb
  // (there is no separate route per tab), so the tab state has to round-trip through the URL
  // rather than living only in component state - initial tab on load/refresh, and kept in sync
  // on every switch below.
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") === REPORT_TAB.FEED ? REPORT_TAB.FEED : REPORT_TAB.USERS
  );
  const [userReports, setUserReports] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [userStatus, setUserStatus] = useState("");
  const [feedContentType, setFeedContentType] = useState("");
  const [tableKey, setTableKey] = useState(0);

  const isFeedTab = activeTab === REPORT_TAB.FEED;

  const searchTimeout = useRef(null);
  const dateTimeout = useRef(null);
  const isFiltering = useRef(false);

  const userStatusOptions = constants.status.reportedUserStatusOptions.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const feedContentTypeOptions = constants.status.reportedFeedContentTypeOptions.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      if (dateTimeout.current) {
        clearTimeout(dateTimeout.current);
      }
    };
  }, []);

  const buildFilters = useCallback(
    () => ({
      start_date: startDate,
      end_date: endDate,
      // user_status only has meaning on the users tab, feed_content_type only on the feed tab -
      // the backend ignores the other, but each is still omitted on the tab it doesn't apply to
      // rather than sent and silently ignored.
      ...(isFeedTab ? { feed_content_type: feedContentType } : { user_status: userStatus }),
      content_type: activeTab,
    }),
    [startDate, endDate, userStatus, feedContentType, activeTab, isFeedTab]
  );

  const fetchUserReports = useCallback(
    async (page = 1, search = "", filters = {}) => {
      setLoading(true);
      try {
        const params = {
          page,
          limit: paginationConstants.defaultPageSize,
          search,
          ...filters,
        };

        const response = await reportReasonService.getReportReasons(params);
        const reports = response?.data?.userReports || response?.userReports;

        if (reports) {
          setUserReports(reports);

          const paginationData = response?.data?.pagination || response?.pagination;
          if (paginationData) {
            const perPage =
              parseInt(paginationData.per_page, 10) ||
              paginationConstants.defaultPageSize;
            const total =
              parseInt(
                paginationData.total_transactions ||
                  paginationData.total ||
                  paginationData.total_items,
                10
              ) || 0;

            setPagination({
              ...paginationData,
              total,
              per_page: perPage,
              count: total,
              page: parseInt(paginationData.page, 10) || page,
              total_pages:
                parseInt(paginationData.total_pages, 10) ||
                Math.ceil(total / perPage) ||
                1,
            });
          }
        } else {
          setUserReports([]);
          setPagination(null);
        }
      } catch (error) {
        console.error("Error fetching reported users:", error);
        toast.error(t(reportedUsers.messages.fetch_failed));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    fetchUserReports(currentPage, searchText, buildFilters());
  }, [
    currentPage,
    searchText,
    startDate,
    endDate,
    userStatus,
    feedContentType,
    activeTab,
    fetchUserReports,
    buildFilters,
  ]);

  const handleTabChange = (tab) => {
    if (tab === activeTab) {
      return;
    }

    isFiltering.current = true;
    setActiveTab(tab);
    setSearchParams(tab === REPORT_TAB.FEED ? { tab } : {}, { replace: true });
    setTempSearchText("");
    setSearchText("");
    setStartDate("");
    setEndDate("");
    setUserStatus("");
    setFeedContentType("");
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setTempSearchText(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      isFiltering.current = true;
      setSearchText(value);
      setCurrentPage(1);
      setTableKey((prev) => prev + 1);

      setTimeout(() => {
        isFiltering.current = false;
      }, 200);
    }, 500);
  };

  const handleDateChange = (dateType, value) => {
    if (dateTimeout.current) {
      clearTimeout(dateTimeout.current);
    }

    dateTimeout.current = setTimeout(() => {
      isFiltering.current = true;

      if (dateType === "start") {
        setStartDate(value);
        if (endDate && endDate < value) {
          setEndDate(value);
        }
      } else if (value < startDate) {
        setEndDate(startDate);
      } else {
        setEndDate(value);
      }

      setCurrentPage(1);
      setTableKey((prev) => prev + 1);

      setTimeout(() => {
        isFiltering.current = false;
      }, 200);
    }, 300);
  };

  const handleClearFilter = () => {
    isFiltering.current = true;

    setTempSearchText("");
    setSearchText("");
    setStartDate("");
    setEndDate("");
    setUserStatus("");
    setFeedContentType("");
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const refreshList = () => {
    fetchUserReports(currentPage, searchText, buildFilters());
  };

  const getReportStatusLabel = (reportStatus) => {
    const normalizedStatus = (reportStatus || "").toLowerCase();
    if (normalizedStatus === "dismissed") {
      return t(reportedUsers.report_status.dismissed);
    }
    if (normalizedStatus === "resolved") {
      return t(reportedUsers.report_status.resolved);
    }
    return t(reportedUsers.report_status.pending);
  };

  const getReportStatusBadgeClass = (reportStatus) => {
    const normalizedStatus = (reportStatus || "").toLowerCase();
    if (normalizedStatus === "dismissed") {
      return "bg-secondary";
    }
    if (normalizedStatus === "resolved") {
      return "bg-success";
    }
    return "bg-warning text-dark";
  };

  const handleDelete = (report, contentType = "user") => {
    const reportStatus = (report.report_status || report.reportStatus || "").toLowerCase();
    if (reportStatus === "dismissed") {
      return;
    }

    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(reportedUsers.delete_popup.heading)}</h2>
            <p className="text-center mb-2">{t(reportedUsers.delete_popup.message)}</p>
            <p className="text-center mb-3">{t(reportedUsers.delete_popup.message_2)}</p>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={async () => {
                  try {
                    await reportReasonService.deleteReportReason(report.id, contentType);
                    toast.success(t(reportedUsers.messages.delete_success));
                    setUserReports((prev) =>
                      prev.map((r) =>
                        r.id === report.id
                          ? { ...r, report_status: "dismissed", reportStatus: "dismissed" }
                          : r
                      )
                    );
                    onClose();
                  } catch (error) {
                    console.error("Error dismissing report:", error);
                    toast.error(t(reportedUsers.messages.delete_failed));
                  }
                }}
              >
                {t(reportedUsers.delete_popup.yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(reportedUsers.delete_popup.no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const handleDeactivate = (report, contentType = "user") => {
    const reportedAdminStatus = (
      report.reported_admin_status || report.reportedAdminStatus || ""
    ).toLowerCase();
    const reportStatus = (report.report_status || report.reportStatus || "").toLowerCase();

    if (
      reportedAdminStatus === status.apiStatus.deactivated ||
      reportStatus === "resolved"
    ) {
      return;
    }

    confirmAlert({
      customUI: ({ onClose }) => (
        <Formik
          initialValues={{ reason: "" }}
          validationSchema={UserMasterValidationClass.deactivateUserValidation(t)}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await reportReasonService.deactivateReportedUser(report.id, values.reason, contentType);
              toast.success(t(reportedUsers.messages.deactivate_success));
              refreshList();
              onClose();
            } catch (error) {
              console.error("Error deactivating reported user:", error);
              toast.error(
                error.response?.data?.message || t(reportedUsers.messages.deactivate_failed)
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <div className="alert-box admin-status-popup">
              <div className="alert-popup">
                <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
                <h2 className="alert-title">{t(reportedUsers.deactivate_popup.heading)}</h2>
                <p className="text-center mb-3">
                  {t(reportedUsers.deactivate_popup.message)}
                </p>
                <div className="mb-3 text-start">
                  <CFormLabel htmlFor="deactivateReason">
                    {t(reportedUsers.deactivate_popup.reason_label)} *
                  </CFormLabel>
                  <CFormTextarea
                    id="deactivateReason"
                    name="reason"
                    rows={3}
                    placeholder={t(reportedUsers.deactivate_popup.reason_placeholder)}
                    value={values.reason}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={touched.reason && !!errors.reason}
                  />
                  {touched.reason && errors.reason && (
                    <div className="text-danger small mt-1">{errors.reason}</div>
                  )}
                </div>
                <div className="d-flex gap-2 justify-content-center flex-nowrap">
                  <CButton
                    color="primary"
                    className="table-delete-button"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                  >
                    {t(reportedUsers.deactivate_popup.yes_btn_text)}
                  </CButton>
                  <CButton
                    className="table-primary-button"
                    color="danger"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    {t(reportedUsers.deactivate_popup.no_btn_text)}
                  </CButton>
                </div>
              </div>
            </div>
          )}
        </Formik>
      ),
    });
  };

  // Reactivates the reported content's owner (or the reported user, on the Users tab) via the
  // same generic account-status endpoint User Management uses - reactivation is an account-level
  // action independent of report_status, so it is offered whenever the account is deactivated,
  // regardless of whether the report itself has since been dismissed or resolved. A full
  // refreshList (rather than patching just the clicked row) is used because the same account can
  // appear on several rows at once (multiple reports against the same user/content owner), and
  // all of them need to reflect the change together.
  // Restrict (read-only mode) acts on the account, not on the report, so report_status is untouched and a
  // full refreshList keeps every row of the same account in step.
  const handleRestrict = (report) => {
    const reportedUuid = report.reported_uuid;
    if (!reportedUuid) return;

    showRestrictDialog({
      t,
      onConfirm: async (payload) => {
        try {
          const response = await UserMasterService.restrict({ id: reportedUuid, ...payload });
          toast.success(response?.message || t(userRestriction.restrict_submit));
          refreshList();
        } catch (error) {
          toast.error(error.response?.data?.message || error.message);
        }
      },
    });
  };

  const handleLiftRestriction = (report) => {
    const reportedUuid = report.reported_uuid;
    if (!reportedUuid) return;

    showConfirmDialog({
      t,
      texts: { heading: userRestriction.lift_heading, message: userRestriction.lift_message },
      onConfirm: async () => {
        try {
          const response = await UserMasterService.liftRestriction(reportedUuid);
          toast.success(response?.message || t(userRestriction.lift_restriction_action));
          refreshList();
        } catch (error) {
          toast.error(error.response?.data?.message || error.message);
        }
      },
    });
  };

  const handleReactivate = (report) => {
    const reportedUuid = report.reported_uuid;

    if (!reportedUuid) {
      return;
    }

    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(reportedUsers.reactivate_popup.heading)}</h2>
            <p className="text-center mb-3">{t(reportedUsers.reactivate_popup.message)}</p>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={async () => {
                  try {
                    await UserMasterService.updateStatus({
                      id: reportedUuid,
                      type: "admin",
                      status: "active",
                    });
                    toast.success(t(reportedUsers.messages.reactivate_success));
                    refreshList();
                    onClose();
                  } catch (error) {
                    console.error("Error reactivating reported user:", error);
                    toast.error(
                      error.response?.data?.message || t(reportedUsers.messages.reactivate_failed)
                    );
                  }
                }}
              >
                {t(reportedUsers.reactivate_popup.yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(reportedUsers.reactivate_popup.no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const userColumns = useMemo(
    () => [
      {
        id: "updated_at",
        header: t(reportedUsers.table.report_date_col_heading),
        accessorKey: "updated_at",
        cell: ({ row }) => (
          <TruncateCell
            text={formatReportDateTime(row.original) || t(reportedUsers.defaults.na)}
          />
        ),
      },
      {
        id: "reporter_name",
        header: t(reportedUsers.table.reporter_name_col_heading),
        accessorKey: "reporter_name",
        cell: ({ row }) => (
          <TruncateCell text={row.original.reporter_name || t(reportedUsers.defaults.na)} />
        ),
      },
      {
        id: "reporter_username",
        header: t(reportedUsers.table.reporter_username_col_heading),
        accessorKey: "reporter_username",
        cell: ({ row }) => (
          <TruncateCell text={row.original.reporter_username || t(reportedUsers.defaults.na)} />
        ),
      },
      {
        id: "reported_name",
        header: t(reportedUsers.table.reported_name_col_heading),
        accessorKey: "reported_name",
        cell: ({ row }) => (
          <TruncateCell text={row.original.reported_name || t(reportedUsers.defaults.na)} />
        ),
      },
      {
        id: "reported_username",
        header: t(reportedUsers.table.reported_username_col_heading),
        accessorKey: "reported_username",
        cell: ({ row }) => (
          <TruncateCell text={row.original.reported_username || t(reportedUsers.defaults.na)} />
        ),
      },
      {
        id: "reason_title",
        header: t(reportedUsers.table.reason_title_col_heading),
        accessorKey: "reason_title",
        cell: ({ row }) => (
          <TruncateCell
            text={row.original.reason_title || t(reportedUsers.defaults.na)}
            className="reported-users-cell-text fw-semibold"
          />
        ),
      },
      {
        id: "report_description",
        header: t(reportedUsers.table.reason_description_col_heading),
        accessorKey: "report_description",
        cell: ({ row }) => (
          <DescriptionCell
            text={getReportDescription(row.original) || t(reportedUsers.defaults.na)}
          />
        ),
      },
      {
        id: "reported_admin_status",
        header: t(reportedUsers.table.reported_status_col_heading),
        accessorKey: "reported_admin_status",
        cell: ({ row }) => {
          const adminStatus = (
            row.original.reported_admin_status || row.original.reportedAdminStatus || ""
          ).toLowerCase();
          const isDeactivated = adminStatus === status.apiStatus.deactivated;
          if (!isDeactivated && isApiTrue(row.original.is_restricted)) {
            return (
              <span className="badge bg-warning text-dark" title={row.original.restriction_reason || ""}>
                {t(userRestriction.status_restricted)}
              </span>
            );
          }
          return (
            <span className={isDeactivated ? "text-danger" : "text-success"}>
              {isDeactivated
                ? t(reportedUsers.user_status.deactivated)
                : t(reportedUsers.user_status.active)}
            </span>
          );
        },
      },
      {
        id: "report_status",
        header: t(reportedUsers.table.report_status_col_heading),
        accessorKey: "report_status",
        cell: ({ row }) => {
          const reportStatus = row.original.report_status || row.original.reportStatus;
          return (
            <span className={`badge ${getReportStatusBadgeClass(reportStatus)}`}>
              {getReportStatusLabel(reportStatus)}
            </span>
          );
        },
      },
      buildActionsColumn("user"),
    ],
    [t]
  );

  // Shared Actions column for both tabs - Dismiss/Deactivate/Reactivate work identically whether
  // the row is a user_reports row or a feed_post_reports one; only `contentType` (which table
  // report.id refers to) needs to be threaded through to the report-status API calls, via
  // handleDelete/handleDeactivate's second argument. Reactivate ignores contentType entirely -
  // it acts on the account directly via the User Management endpoint, not on the report.
  function buildActionsColumn(contentType) {
    return {
      id: "actions",
      header: t(reportedUsers.table.actions_col_heading),
      accessorKey: "actions",
      cell: ({ row }) => {
        const report = row.original;
        const reportStatus = (report.report_status || report.reportStatus || "").toLowerCase();
        const reportedAdminStatus = (
          report.reported_admin_status || report.reportedAdminStatus || ""
        ).toLowerCase();
        const isDeactivated = reportedAdminStatus === status.apiStatus.deactivated;
        // A dismissed report was judged invalid - no action of any kind is offered on that row
        // from here on. A resolved report still allows Deactivate/Reactivate, since the account
        // itself may change state again after the report was actioned.
        const isDismissed = reportStatus === "dismissed";
        const canDelete = !isDismissed;
        const canDeactivate = !isDismissed && !isDeactivated;
        const canReactivate = !isDismissed && isDeactivated;
        const isRestricted = isApiTrue(report.is_restricted);
        const canRestrict = !isDismissed && !isRestricted;
        // Lifting stays available on a dismissed report: the restriction belongs to the account.
        const canLiftRestriction = isRestricted;

        return (
          <div className="d-flex gap-2">
            {canRestrict && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`restrict-${contentType}-${report.id}`}>
                    {t(userRestriction.restrict_tooltip)}
                  </Tooltip>
                }
              >
                <CButton color="warning" size="sm" onClick={() => handleRestrict(report)}>
                  <FontAwesomeIcon icon={faBan} />
                </CButton>
              </OverlayTrigger>
            )}
            {canLiftRestriction && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`lift-${contentType}-${report.id}`}>
                    {t(userRestriction.lift_tooltip)}
                  </Tooltip>
                }
              >
                <CButton color="info" size="sm" onClick={() => handleLiftRestriction(report)}>
                  <FontAwesomeIcon icon={faUnlock} />
                </CButton>
              </OverlayTrigger>
            )}
            {canDelete && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`delete-${contentType}-${report.id}`}>
                    {t(reportedUsers.action_tooltip.delete)}
                  </Tooltip>
                }
              >
                <CButton
                  color="danger"
                  size="sm"
                  onClick={() => handleDelete(report, contentType)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </CButton>
              </OverlayTrigger>
            )}
            {canDeactivate && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`deactivate-${contentType}-${report.id}`}>
                    {t(reportedUsers.action_tooltip.deactivate)}
                  </Tooltip>
                }
              >
                <CButton
                  color="warning"
                  size="sm"
                  onClick={() => handleDeactivate(report, contentType)}
                >
                  <FontAwesomeIcon icon={faUserSlash} />
                </CButton>
              </OverlayTrigger>
            )}
            {canReactivate && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`reactivate-${contentType}-${report.id}`}>
                    {t(reportedUsers.action_tooltip.reactivate)}
                  </Tooltip>
                }
              >
                <CButton
                  color="success"
                  size="sm"
                  onClick={() => handleReactivate(report)}
                >
                  <FontAwesomeIcon icon={faUserCheck} />
                </CButton>
              </OverlayTrigger>
            )}
          </div>
        );
      },
    };
  }

  // The "Reported Post and Stories" tab. Reused keys where the backend's shape already matches -
  // reported_name/reported_username/reported_admin_status are the CONTENT'S AUTHOR on this tab,
  // not a separately-reported user, so "Reported User" becomes "Content Owner". Dismiss/
  // Deactivate reuse the same Actions column as the Users tab, passing contentType="feed" so
  // the API calls target feed_post_reports (and the reported content's author) instead of
  // user_reports.
  // deliberately no Actions column here: dismiss/deactivate call endpoints that operate on
  // user_reports rows and do not understand feed_post_reports, so wiring them up against this
  // tab would either error or silently act on the wrong table.
  const feedColumns = useMemo(
    () => [
      {
        id: "updated_at",
        header: t(reportedUsers.table.report_date_col_heading),
        accessorKey: "updated_at",
        cell: ({ row }) => (
          <TruncateCell
            text={formatReportDateTime(row.original) || t(reportedUsers.defaults.na)}
          />
        ),
      },
      {
        id: "reporter_name",
        header: t(reportedUsers.table.reporter_name_col_heading),
        accessorKey: "reporter_name",
        cell: ({ row }) => (
          <TruncateCell text={row.original.reporter_name || t(reportedUsers.defaults.na)} />
        ),
      },
      {
        id: "reporter_username",
        header: t(reportedUsers.table.reporter_username_col_heading),
        accessorKey: "reporter_username",
        cell: ({ row }) => (
          <TruncateCell text={row.original.reporter_username || t(reportedUsers.defaults.na)} />
        ),
      },
      {
        id: "reported_content",
        header: t(reportedUsers.table.reported_content_col_heading),
        accessorKey: "content_preview",
        cell: ({ row }) => <ReportedContentCell row={row.original} t={t} />,
      },
      {
        id: "content_owner",
        header: t(reportedUsers.table.content_owner_col_heading),
        accessorKey: "reported_name",
        cell: ({ row }) => (
          <div className="d-flex flex-column">
            <TruncateCell text={row.original.reported_name || t(reportedUsers.defaults.na)} />
            {row.original.reported_username && (
              <span className="reported-users-cell-text text-body-secondary">
                @{row.original.reported_username}
              </span>
            )}
          </div>
        ),
      },
      {
        id: "reason_title",
        header: t(reportedUsers.table.reason_title_col_heading),
        accessorKey: "reason_title",
        cell: ({ row }) => (
          <TruncateCell
            text={row.original.reason_title || t(reportedUsers.defaults.na)}
            className="reported-users-cell-text fw-semibold"
          />
        ),
      },
      {
        id: "report_description",
        header: t(reportedUsers.table.reason_description_col_heading),
        accessorKey: "report_description",
        cell: ({ row }) => (
          <DescriptionCell
            text={getReportDescription(row.original) || t(reportedUsers.defaults.na)}
          />
        ),
      },
      {
        id: "reported_admin_status",
        header: t(reportedUsers.table.reported_status_col_heading),
        accessorKey: "reported_admin_status",
        cell: ({ row }) => {
          const adminStatus = (
            row.original.reported_admin_status || row.original.reportedAdminStatus || ""
          ).toLowerCase();
          const isDeactivated = adminStatus === status.apiStatus.deactivated;
          if (!isDeactivated && isApiTrue(row.original.is_restricted)) {
            return (
              <span className="badge bg-warning text-dark" title={row.original.restriction_reason || ""}>
                {t(userRestriction.status_restricted)}
              </span>
            );
          }
          return (
            <span className={isDeactivated ? "text-danger" : "text-success"}>
              {isDeactivated
                ? t(reportedUsers.user_status.deactivated)
                : t(reportedUsers.user_status.active)}
            </span>
          );
        },
      },
      {
        id: "report_status",
        header: t(reportedUsers.table.report_status_col_heading),
        accessorKey: "report_status",
        cell: ({ row }) => {
          const reportStatus = row.original.report_status || row.original.reportStatus;
          return (
            <span className={`badge ${getReportStatusBadgeClass(reportStatus)}`}>
              {getReportStatusLabel(reportStatus)}
            </span>
          );
        },
      },
      buildActionsColumn("feed"),
    ],
    [t]
  );

  const columns = isFeedTab ? feedColumns : userColumns;

  return (
    <div className="reported-users-list">
      {/* The breadcrumb (App.jsx#ReportedUsersRoute) now restates the active tab instead of a
          heading here - it reads the same `tab` query param handleTabChange keeps in sync. */}

      <CNav variant="tabs" role="tablist" className="mb-3 reported-users-tabs">
        <CNavItem>
          <CNavLink
            active={activeTab === REPORT_TAB.USERS}
            onClick={() => handleTabChange(REPORT_TAB.USERS)}
            style={{ cursor: "pointer" }}
          >
            {t(reportedUsers.tabs.users)}
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink
            active={activeTab === REPORT_TAB.FEED}
            onClick={() => handleTabChange(REPORT_TAB.FEED)}
            style={{ cursor: "pointer" }}
          >
            {t(reportedUsers.tabs.feed)}
          </CNavLink>
        </CNavItem>
      </CNav>

      <CRow className="mb-4">
        <CCol lg={12} className="user-list-filters d-flex gap-2 justify-content-end flex-wrap">
          <CFormInput
            placeholder={t(
              isFeedTab
                ? reportedUsers.search_placeholder_feed
                : reportedUsers.search_placeholder
            )}
            type="text"
            value={tempSearchText}
            onChange={handleSearch}
            onKeyUp={handleSearch}
            className="filter-field-space form-control-sm same-input-cls"
            style={{
              maxWidth: "300px",
              ...filterInputStyle,
            }}
          />

          {/* Filters by the REPORTED USER's account status - meaningless on the feed tab, where
              the backend does not scope feed_post_reports by it, so it is hidden there rather
              than shown and silently ignored. */}
          {!isFeedTab && (
            <CustomDropdown
              className="filter-field-space form-control-sm"
              style={{ maxWidth: "200px", ...filterInputStyle }}
              value={userStatus}
              onChange={(e) => {
                isFiltering.current = true;
                setUserStatus(e.target.value);
                setCurrentPage(1);
                setTableKey((prev) => prev + 1);
                setTimeout(() => {
                  isFiltering.current = false;
                }, 200);
              }}
              options={userStatusOptions}
              placeholder={t(reportedUsers.user_status_option)}
            />
          )}

          {/* Filters by content type (post/story) - only meaningful on the feed tab, which
              covers both types in one list. Mirrors the user_status dropdown above. */}
          {isFeedTab && (
            <CustomDropdown
              className="filter-field-space form-control-sm"
              style={{ maxWidth: "200px", ...filterInputStyle }}
              value={feedContentType}
              onChange={(e) => {
                isFiltering.current = true;
                setFeedContentType(e.target.value);
                setCurrentPage(1);
                setTableKey((prev) => prev + 1);
                setTimeout(() => {
                  isFiltering.current = false;
                }, 200);
              }}
              options={feedContentTypeOptions}
              placeholder={t(reportedUsers.content_type_option)}
            />
          )}

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="reportedUsersStartDate" className="m-0 text-white">
              {t(user.list.table.start_date_label)}
            </CFormLabel>
            <CFormInput
              type="date"
              id="reportedUsersStartDate"
              value={startDate}
              onChange={(e) => handleDateChange("start", e.target.value)}
              className="form-control same-input-cls"
              style={{ width: "155px", ...filterInputStyle }}
            />
          </div>

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="reportedUsersEndDate" className="m-0 text-white">
              {t(user.list.table.end_date_label)}
            </CFormLabel>
            <CFormInput
              type="date"
              id="reportedUsersEndDate"
              value={endDate}
              onChange={(e) => handleDateChange("end", e.target.value)}
              min={startDate}
              className="form-control same-input-cls"
              style={{ width: "155px", ...filterInputStyle }}
            />
          </div>

          {(tempSearchText || startDate || endDate || userStatus || feedContentType) && (
            <CButton
              color="primary"
              type="button"
              onClick={handleClearFilter}
              className="filter-field-space"
              style={filterInputStyle}
            >
              {t(reportedUsers.clear_filter_btn_text)}
            </CButton>
          )}
        </CCol>
      </CRow>

      <TNTable
        key={tableKey}
        columns={columns}
        data={userReports}
        paginationData={pagination}
        onSelectPage={(pageIndex) => {
          if (isFiltering.current) {
            return;
          }

          setCurrentPage(pageIndex + 1);
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }}
        pageIndexGet={currentPage - 1}
        isLoading={loading}
        t={t}
        responsive={false}
        className={`reported-users-table${isFeedTab ? " reported-feed-table" : ""}`}
      />
    </div>
  );
};

ReportedUsersList.breadCrumbArray = [
  {
    name: breadcrumb_key.reportedUsers?.list,
    pathname: masters.reportedUsers.list,
    active: true,
  },
];

export default ReportedUsersList;
